import { useEffect } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import type { NotificationOut } from 'connectors/notifications/types.gen';
import {
  getNotificationCountsApiNotificationsCountsGet,
  listNotificationsApiNotificationsGet,
  patchAllNotificationsApiNotificationsPatch,
  patchNotificationApiNotificationsNotificationIdPatch,
} from './notificationsApi';
import { showNotification } from 'common/function';
import { parseFrame } from './sseFrames';

export type { NotificationOut };

const BASE_URL = process.env.NEXT_PUBLIC_NOTIFICATIONS_API_ENDPOINT || '/notifications';

async function patchNotification(id: string, read: boolean): Promise<void> {
  await patchNotificationApiNotificationsNotificationIdPatch({
    path: { notification_id: id },
    body: { read },
    throwOnError: true,
  });
}

async function patchAllNotifications(): Promise<void> {
  await patchAllNotificationsApiNotificationsPatch({ body: { read: true }, throwOnError: true });
}

/** Root key: invalidating it covers counts and both tab lists. */
export const NOTIFICATIONS_KEY = ['notifications'] as const;

export type NotificationsTab = 'unread' | 'all';

export const NOTIFICATIONS_COUNTS_KEY = [...NOTIFICATIONS_KEY, 'counts'] as const;
export const notificationsListKey = (tab: NotificationsTab) => [...NOTIFICATIONS_KEY, 'list', tab] as const;

const PAGE_SIZE = 25;
// Caps how deep the drawer paginates (and so the refetch cost of live updates
// while it is open). Implemented as a getNextPageParam cutoff, NOT react-query's
// maxPages: maxPages evicts the offset-0 page (the newest rows) on overflow and
// refetches would never recover them. Deep history belongs to a future
// full-page inbox, not the drawer.
const MAX_PAGES = 8;
const COUNTS_REFETCH_INTERVAL_MS = 5 * 60_000;

export function useUnreadCount() {
  return useQuery({
    queryKey: NOTIFICATIONS_COUNTS_KEY,
    queryFn: async () => {
      const { data } = await getNotificationCountsApiNotificationsCountsGet({ throwOnError: true });
      return data.unread;
    },
    // Belt against missed SSE publishes: the global default disables focus
    // refetch; notifications opt back in (cheap, always-mounted query).
    refetchOnWindowFocus: true,
    refetchInterval: COUNTS_REFETCH_INTERVAL_MS,
  });
}

async function fetchNotificationsPage(tab: NotificationsTab, offset: number): Promise<NotificationOut[]> {
  const { data } = await listNotificationsApiNotificationsGet({
    query:
      tab === 'unread'
        ? { unread_only: true, resolved: false, offset, limit: PAGE_SIZE }
        : { include_expired: true, offset, limit: PAGE_SIZE },
    throwOnError: true,
  });
  return data.data;
}

export type NotificationPages = InfiniteData<NotificationOut[], number>;

/** Offset pages refetch sequentially, so a concurrent insert can repeat a row
 * across a page boundary — drop duplicate ids when flattening. */
function dedupeById(data: NotificationPages): NotificationOut[] {
  const seen = new Set<string>();
  return data.pages.flat().filter((notification) => {
    if (seen.has(notification.id)) {
      return false;
    }
    seen.add(notification.id);
    return true;
  });
}

export function useNotificationsList(tab: NotificationsTab) {
  return useInfiniteQuery({
    queryKey: notificationsListKey(tab),
    queryFn: ({ pageParam }) => fetchNotificationsPage(tab, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) =>
      lastPage.length === PAGE_SIZE && allPages.length < MAX_PAGES ? lastPageParam + PAGE_SIZE : undefined,
    refetchOnWindowFocus: true,
    // Lists mount only while the drawer is open; dropping the cache on close
    // means every open starts from a fresh offset-0 fetch instead of
    // re-paginating a stale snapshot.
    gcTime: 0,
    select: dedupeById,
  });
}

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const RECONNECT_JITTER_MS = 1_000;
const INVALIDATE_DEBOUNCE_MS = 300;
// Spreads refetches across clients when a workspace broadcast wakes everyone at once.
const INVALIDATE_JITTER_MS = 200;
// How long a backgrounded tab keeps its SSE stream open before closing it.
// Defaults to 5 min; overridable via env for local testing (e.g. 15000 to
// watch close/reopen quickly). Prod leaves the env unset → the 5-min default.
const HIDDEN_DISCONNECT_GRACE_MS = Number(process.env.NEXT_PUBLIC_NOTIFICATIONS_HIDDEN_GRACE_MS) || 5 * 60_000;

/**
 * Owns the SSE connection and maps every frame to a query invalidation.
 * Mount exactly once at the app shell (today: NotificationsDrawer in the
 * header, a singleton).
 *
 * Invalidation-first contract: REST is the only source of state. Any frame -
 * `connected` included - means "ask the server again". react-query then
 * refetches only the mounted queries: counts (always), plus the open drawer
 * tab's list. Hidden browser tabs mark queries stale without fetching
 * (refetchOnWindowFocus picks them up on activation), and drop the connection
 * after a grace period to keep N-tabs users from holding N idle streams.
 *
 * Connection lifecycle (unchanged from the snapshot era):
 * - transient drop -> readyState CONNECTING: the browser retries on its own.
 * - fatal close -> readyState CLOSED (e.g. 401): recreate with capped
 *   exponential backoff + jitter.
 * - unmount -> close the connection and cancel all timers.
 */
export function useNotificationsSSE() {
  const queryClient = useQueryClient();

  useEffect(() => {
    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
    let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let firstConnect = true;
    let disposed = false;

    const scheduleInvalidate = () => {
      if (document.visibilityState === 'hidden') {
        // Background tab: mark stale, never fetch. refetchOnWindowFocus
        // refetches stale notification queries when the tab activates.
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY, refetchType: 'none' });
        return;
      }
      if (invalidateTimer !== null) { clearTimeout(invalidateTimer); }
      invalidateTimer = setTimeout(() => {
        invalidateTimer = null;
        if (document.visibilityState === 'hidden') {
          // Tab hid while the debounce was pending: degrade to mark-stale-only.
          queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY, refetchType: 'none' });
          return;
        }
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      }, INVALIDATE_DEBOUNCE_MS + Math.random() * INVALIDATE_JITTER_MS);
    };

    const connect = () => {
      if (disposed) { return; }
      es = new EventSource(`${BASE_URL}/api/notifications/stream`, { withCredentials: true });

      es.onmessage = (event: MessageEvent<string>) => {
        const frame = parseFrame(event.data);
        if (!frame) { return; }
        if (frame.event === 'connected') {
          attempt = 0;
          if (firstConnect) {
            // Initial mount: the queries are fetching fresh right now - an
            // invalidation would only duplicate those requests.
            firstConnect = false;
            return;
          }
        }
        scheduleInvalidate();
      };

      es.onerror = () => {
        if (!es || es.readyState !== EventSource.CLOSED) {
          return; // CONNECTING: browser-managed retry is already underway.
        }
        es.close();
        es = null;
        const delay =
          Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS) + Math.random() * RECONNECT_JITTER_MS;
        console.warn('[notifications] SSE stream closed; reconnecting in %dms (attempt %d)', Math.round(delay), attempt + 1);
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    const disconnect = () => {
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      es?.close();
      es = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (hiddenTimer === null) {
          hiddenTimer = setTimeout(() => {
            hiddenTimer = null;
            disconnect();
          }, HIDDEN_DISCONNECT_GRACE_MS);
        }
        return;
      }
      if (hiddenTimer !== null) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
      if (es === null && retryTimer === null) {
        // Reopened after a hidden-grace disconnect: the server's `connected`
        // frame triggers the catch-up invalidation.
        attempt = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    connect();

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (retryTimer !== null) { clearTimeout(retryTimer); }
      if (invalidateTimer !== null) { clearTimeout(invalidateTimer); }
      if (hiddenTimer !== null) { clearTimeout(hiddenTimer); }
      es?.close();
    };
  }, [queryClient]);
}

function patchReadAtInLists(queryClient: QueryClient, id: string, readAt: string | null) {
  for (const tab of ['unread', 'all'] as const) {
    queryClient.setQueryData<NotificationPages>(notificationsListKey(tab), (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((page) =>
              page.map((notification) =>
                notification.id === id ? { ...notification, read_at: readAt } : notification,
              ),
            ),
          }
        : old,
    );
  }
}

type MutationSnapshot = { snapshot: [readonly unknown[], unknown][] };

async function snapshotNotificationCaches(queryClient: QueryClient): Promise<MutationSnapshot> {
  await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
  return { snapshot: queryClient.getQueriesData({ queryKey: NOTIFICATIONS_KEY }) };
}

function restoreNotificationCaches(queryClient: QueryClient, context: MutationSnapshot | undefined) {
  context?.snapshot.forEach(([queryKey, data]) => queryClient.setQueryData([...queryKey], data));
}

/** Rollback alone is invisible (the toggle just snaps back) — tell the user the action failed. */
function rollbackWithToast(queryClient: QueryClient, context: MutationSnapshot | undefined, title: string) {
  restoreNotificationCaches(queryClient, context);
  showNotification('', title, 'error');
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patchNotification(id, true),
    onMutate: async (id) => {
      const context = await snapshotNotificationCaches(queryClient);
      patchReadAtInLists(queryClient, id, new Date().toISOString());
      queryClient.setQueryData<number>(NOTIFICATIONS_COUNTS_KEY, (old = 0) => Math.max(0, old - 1));
      return context;
    },
    onError: (_error, _id, context) =>
      rollbackWithToast(queryClient, context, 'Failed to mark the notification as read'),
    // No confirm-invalidate here: the server publishes a read_state_changed echo
    // for this PATCH, and useNotificationsSSE turns that into the single, debounced
    // refetch. Re-adding onSettled invalidate double-fetches /counts and the list
    // (the two fire ~debounce apart, so they don't coalesce). onError still rolls
    // the optimistic update back; a dropped echo self-heals on the next resync.
  });
}

export function useMarkNotificationUnread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patchNotification(id, false),
    onMutate: async (id) => {
      const context = await snapshotNotificationCaches(queryClient);
      patchReadAtInLists(queryClient, id, null);
      queryClient.setQueryData<number>(NOTIFICATIONS_COUNTS_KEY, (old = 0) => old + 1);
      return context;
    },
    onError: (_error, _id, context) =>
      rollbackWithToast(queryClient, context, 'Failed to mark the notification as unread'),
    // Confirm refetch comes from the SSE echo of this PATCH (single, debounced);
    // see useNotificationsSSE / useMarkNotificationRead.
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchAllNotifications,
    onMutate: async () => {
      const context = await snapshotNotificationCaches(queryClient);
      const readAt = new Date().toISOString();
      for (const tab of ['unread', 'all'] as const) {
        queryClient.setQueryData<NotificationPages>(notificationsListKey(tab), (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) =>
                  page.map((notification) =>
                    notification.read_at ? notification : { ...notification, read_at: readAt },
                  ),
                ),
              }
            : old,
        );
      }
      queryClient.setQueryData<number>(NOTIFICATIONS_COUNTS_KEY, 0);
      return context;
    },
    onError: (_error, _variables, context) =>
      rollbackWithToast(queryClient, context, 'Failed to mark all notifications as read'),
    // Confirm refetch comes from the SSE echo of this PATCH (single, debounced);
    // see useNotificationsSSE / useMarkNotificationRead.
  });
}
